import { assertNever } from '@deltasierra/shared';
import { PopperProps, Fade, Paper, Popper } from '@material-ui/core';
import * as React from 'react';
import styled from 'styled-components';

const StyledPopperWrapper = styled.div<{ $maxWidth?: number }>`
    ${props =>
        props.$maxWidth &&
        `
        max-width: ${props.$maxWidth}px;
    `};
`;

const StyledPaper = styled(Paper)`
    padding: ${props => props.theme.spacing(1)}px;
    border-radius: 2px;
`;

function maxWidthToNumber(maxWidth: HoverPopperPopperMaxWidthType): number | undefined {
    switch (maxWidth) {
        case 'none':
            return undefined;
        case 'small':
            return 200;
        case 'medium':
            return 250;
        case 'large':
            return 500;
        default:
            throw assertNever(maxWidth);
    }
}

export type HoverPopperPopperMaxWidthType = 'large' | 'medium' | 'none' | 'small';

export type HoverPopperPopperProps = {
    anchorEle?: PopperProps['anchorEl'];
    children: React.ReactNode;
    maxWidth: HoverPopperPopperMaxWidthType;
    modifiers?: PopperProps['modifiers'];
    open: PopperProps['open'];
    placement?: PopperProps['placement'];
    transitionTimeout?: number;
    usePaper?: boolean;
};

export function HoverPopperPopper({
    anchorEle,
    children,
    maxWidth,
    modifiers,
    open,
    placement,
    transitionTimeout = 250,
    usePaper = true,
}: HoverPopperPopperProps): JSX.Element {
    const maxWidthNumber = React.useMemo(() => maxWidthToNumber(maxWidth), [maxWidth]);

    const memoChild = React.useMemo(
        () => (usePaper ? <StyledPaper>{children}</StyledPaper> : children),
        [usePaper, children],
    );

    return (
        <Popper anchorEl={anchorEle} modifiers={modifiers} open={open} placement={placement} transition>
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={transitionTimeout}>
                    <StyledPopperWrapper $maxWidth={maxWidthNumber}>{memoChild}</StyledPopperWrapper>
                </Fade>
            )}
        </Popper>
    );
}
HoverPopperPopper.displayName = 'HoverPopperPopper';
